// deps
import {
  Box,
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useRouter } from "next/router";
import { useIntl } from "react-intl";

// libraries
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";

// normalizers
import { usersPasswordResetsSerializeActionFinish } from "@raiden/library/normalizers/users/passwordResets";

// helpers
import {
  useApiError,
  withApiError,
} from "@raiden/library/components/ReactHookForm/ApiError";
import FormController from "@raiden/library/components/ReactHookForm/FormController";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import ApiErrorMessage from "../../../components/ApiErrorMessage";
import useRequest from "@raiden/library/hooks/useRequest";

function AuthPasswordResetStep2({ token, email }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmationPassword, setShowConfirmationPassword] =
    useState(false);

  const router = useRouter();

  const intl = useIntl();

  const { request, isLoading, toastSuccess } = useRequest();

  const form = useForm({
    defaultValues: {
      data: {
        token,
        email,
        password: "",
      },
      _password_confirm: "",
    },
  });
  const { handleSubmit, getValues } = form;

  const { resolveApiError, resetApiErrors } = useApiError();

  /**
   * Gère la soumision du formulaire.
   */
  async function onSubmit(fields) {
    request(
      generateApiUri({
        id: "@usersPasswordResets.actionFinish",
      }),
      {
        method: "POST",
        body: usersPasswordResetsSerializeActionFinish({ fields }),
      },
    )
      .then(function () {
        resetApiErrors();
        toastSuccess({
          title: intl.formatMessage({
            defaultMessage: "Mot de passe réinitialisé",
          }),
        });

        router.replace({ pathname: generateFrontPath({ id: "@account" }) });
      })
      .catch((response) => {
        resolveApiError({ form, response });
      });
  }

  const toggleShowPassword = useCallback(() => {
    setShowPassword((showPassword) => !showPassword);
  }, []);

  const toggleShowConfirmationPassword = useCallback(() => {
    setShowConfirmationPassword(
      (showConfirmationPassword) => !showConfirmationPassword,
    );
  }, []);

  return (
    <Stack spacing="1.5rem" padding="1rem">
      <Text color="gray.700" fontSize="1.5rem" textAlign="center">
        {intl.formatMessage({
          defaultMessage: "Choisissez votre nouveau mot de passe",
        })}
      </Text>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing="1rem">
            <Box>
              <ApiErrorMessage />

              <Text>
                {intl.formatMessage({
                  defaultMessage:
                    "Veuillez choisir votre mot de passe et le confirmer afin de le réinitialiser.",
                })}
              </Text>
            </Box>

            <FormController
              name="data.password"
              label={intl.formatMessage({
                defaultMessage: "Saisissez votre nouveau mot de passe",
              })}
              helperText={intl.formatMessage({
                defaultMessage:
                  "Le mot de passe doit contenir au moins 8 caractères dont 1 symbole.",
              })}
              rules={{
                required: true,
              }}
              renderWithFormControl={(field) => (
                <InputGroup>
                  <Input
                    {...field}
                    borderColor={"gray.400"}
                    _hover={{ borderColor: "gray.500" }}
                    type={showPassword ? "text" : "password"}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Nouveau mot de passe",
                    })}
                  />

                  <InputRightElement width="3.25rem">
                    <HStack spacing=".5rem">
                      <Tooltip
                        label={intl.formatMessage({
                          defaultMessage: "Afficher/masquer le mot de passe",
                        })}>
                        <Button
                          h="1.75rem"
                          type="button"
                          size="sm"
                          onClick={toggleShowPassword}>
                          {showPassword ? (
                            <Icon as={IoEyeOffOutline} />
                          ) : (
                            <Icon as={IoEyeOutline} />
                          )}
                        </Button>
                      </Tooltip>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
              )}
            />

            <FormController
              type="password"
              name="_password_confirm"
              label={intl.formatMessage({
                defaultMessage: "Confirmez votre nouveau mot de passe",
              })}
              rules={{
                required: true,
                validate: (value) =>
                  value !== getValues("data.password")
                    ? intl.formatMessage({
                        defaultMessage:
                          "Les mots de passe ne correspondent pas",
                      })
                    : true,
              }}
              renderWithFormControl={(field) => (
                <InputGroup>
                  <Input
                    {...field}
                    borderColor={"gray.400"}
                    _hover={{ borderColor: "gray.500" }}
                    type={showConfirmationPassword ? "text" : "password"}
                    placeholder={intl.formatMessage({
                      defaultMessage: "Nouveau mot de passe",
                    })}
                  />

                  <InputRightElement width="3.25rem">
                    <HStack spacing=".5rem">
                      <Tooltip
                        label={intl.formatMessage({
                          defaultMessage: "Afficher/masquer le mot de passe",
                        })}>
                        <Button
                          h="1.75rem"
                          type="button"
                          size="sm"
                          onClick={toggleShowConfirmationPassword}>
                          {showConfirmationPassword ? (
                            <Icon as={IoEyeOffOutline} />
                          ) : (
                            <Icon as={IoEyeOutline} />
                          )}
                        </Button>
                      </Tooltip>
                    </HStack>
                  </InputRightElement>
                </InputGroup>
              )}
            />

            <Button
              type="submit"
              w="100%"
              variant="solid"
              isLoading={isLoading}>
              {intl.formatMessage({
                defaultMessage: "Réinitialiser mon mot de passe",
              })}
            </Button>
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );
}

export default withApiError(AuthPasswordResetStep2);
